import React, { useCallback, useState, memo } from 'react';
import toTitleCase from '@hooks/toTitleCase';
import { View, Platform, TouchableOpacity } from 'react-native';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import globalStyles from '@stylesheet';
import { useDispatch } from 'react-redux';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { useDeviceType } from '@hooks/useDeviceType';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text as RNText } from 'react-native';
import getDaysAgo from '@utils/getDaysAgo';
import { SHOW_CALL_MODAL } from '@reduxLocal/actions/types';
import { KnowledgeStackParamList } from '@navigation/types';

export interface RenderProjectProps {
  item: any;
  index: number;
}

const FlowItem = memo(({ item, index }: RenderProjectProps) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const navigation = useNavigation<StackNavigationProp<KnowledgeStackParamList>>();
  const [context, setContext] = useState(false);

  // Calculate flow statistics
  const totalStages = item.actions?.length || 0;
  const totalActions = item.actions?.reduce((total, stage) => {
    return total + (stage.stageActions?.length || 0);
  }, 0) || 0;
  const completionRate = item.analytics?.completionRate || 0;
  const totalExecutions = item.analytics?.totalExecutions || 0;
  const completedExecutions = item.analytics?.completedExecutions || 0;

  // Get flow type badge color
  const getFlowTypeBadgeColors = useCallback((flowType: string) => {
    const colors = {
      onboarding: { light: '#dbeafe', dark: '#1e3a8a', textLight: '#1e40af', textDark: '#93c5fd' },
      support: { light: '#fef3c7', dark: '#78350f', textLight: '#d97706', textDark: '#fbbf24' },
      sales: { light: '#dcfce7', dark: '#14532d', textLight: '#16a34a', textDark: '#86efac' },
      training: { light: '#e0e7ff', dark: '#312e81', textLight: '#4f46e5', textDark: '#a5b4fc' },
      approval: { light: '#fce7f3', dark: '#831843', textLight: '#be185d', textDark: '#f9a8d4' },
      workflow: { light: '#e0f2fe', dark: '#0c4a6e', textLight: '#0284c7', textDark: '#7dd3fc' },
      troubleshooting: { light: '#fed7aa', dark: '#7c2d12', textLight: '#ea580c', textDark: '#fdba74' },
      general: { light: '#f1f5f9', dark: '#1e293b', textLight: '#475569', textDark: '#cbd5e1' }
    };
    return colors[flowType] || colors.general;
  }, []);

  // Get status badge colors
  const getStatusBadgeColors = useCallback((status: string) => {
    const colors = {
      active: { light: '#dcfce7', dark: '#14532d', textLight: '#16a34a', textDark: '#86efac' },
      draft: { light: '#f1f5f9', dark: '#1e293b', textLight: '#475569', textDark: '#cbd5e1' },
      inactive: { light: '#fee2e2', dark: '#7f1d1d', textLight: '#dc2626', textDark: '#fca5a5' },
      testing: { light: '#fef3c7', dark: '#78350f', textLight: '#d97706', textDark: '#fbbf24' },
      archived: { light: '#f3f4f6', dark: '#1f2937', textLight: '#6b7280', textDark: '#9ca3af' }
    };
    return colors[status] || colors.draft;
  }, []);

  const flowTypeBadgeColors = getFlowTypeBadgeColors(item.flowType || 'general');
  const statusBadgeColors = getStatusBadgeColors(item.status || 'draft');

  return (
    <View style={{ padding: Platform.select({ web: 20 }), overflow: 'hidden', flex: 1 }}>
      <TouchableOpacity
        activeOpacity={0.9}
        onLongPress={() => setContext(!context)}
        onPress={() => {
          // Navigate directly to FlowEditor (we're already in FlowStack)
          navigation.navigate('FlowEditor', {
            flowId: item._id,
            mode: 'edit'
          });
        }}
      >
        <View style={[
          globalStyles.modernCard,
          theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
          { marginVertical: 8, marginHorizontal: 8 }
        ]}>
          <View style={{ gap: 16, padding: 16 }}>
            {/* Header Section with Badges */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}>
              <View style={{ flex: 1, gap: 8 }}>
                {/* Badges Row */}
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                  {/* Flow Type Badge */}
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    backgroundColor: theme ? flowTypeBadgeColors.light : flowTypeBadgeColors.dark,
                    borderRadius: 8,
                  }}>
                    <MaterialCommunityIcons
                      name="sitemap"
                      size={12}
                      color={theme ? flowTypeBadgeColors.textLight : flowTypeBadgeColors.textDark}
                    />
                    <RNText style={{
                      color: theme ? flowTypeBadgeColors.textLight : flowTypeBadgeColors.textDark,
                      fontSize: 11,
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {item.flowType || 'general'}
                    </RNText>
                  </View>

                  {/* Status Badge */}
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    backgroundColor: theme ? statusBadgeColors.light : statusBadgeColors.dark,
                    borderRadius: 8,
                  }}>
                    <View style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme ? statusBadgeColors.textLight : statusBadgeColors.textDark
                    }} />
                    <RNText style={{
                      color: theme ? statusBadgeColors.textLight : statusBadgeColors.textDark,
                      fontSize: 11,
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {item.status || 'draft'}
                    </RNText>
                  </View>

                  {/* Voice Call Enabled Badge */}
                  {item.voiceCallConfig?.enabled && (
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      backgroundColor: theme ? '#e0e7ff' : '#312e81',
                      borderRadius: 8,
                    }}>
                      <MaterialIcons
                        name="phone-in-talk"
                        size={12}
                        color={theme ? '#4f46e5' : '#a5b4fc'}
                      />
                      <RNText style={{
                        color: theme ? '#4f46e5' : '#a5b4fc',
                        fontSize: 11,
                        fontWeight: '600'
                      }}>
                        Voice
                      </RNText>
                    </View>
                  )}
                </View>

                <RNText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 18,
                  fontWeight: '700',
                  lineHeight: 24
                }}>
                  {item.title || "Untitled Flow"}
                </RNText>

                <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <MaterialIcons
                      name="event"
                      size={16}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                    <RNText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {getDaysAgo(item.createdAt)}
                    </RNText>
                  </View>

                  {item.version > 1 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <MaterialIcons
                        name="history"
                        size={16}
                        color={theme ? '#64748b' : '#94a3b8'}
                      />
                      <RNText style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 14
                      }}>
                        v{item.version}
                      </RNText>
                    </View>
                  )}
                </View>
              </View>

              <View style={{ marginLeft: 16 }}>
                <AvatarHandler
                  avatarImageStyle={{
                    width: 44,
                    height: 44,
                    borderRadius: 12
                  }}
                  replacementImageStyle={{
                    width: 44,
                    height: 44,
                    borderRadius: 12
                  }}
                  avatar={item.createdBy?.profile_information?.avatar?.img}
                />
              </View>
            </View>

            {/* Description */}
            {item.description && (
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle
              ]}>
                <RNText numberOfLines={2} style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                  lineHeight: 20,
                  padding: 12
                }}>
                  {item.description}
                </RNText>
              </View>
            )}

            {/* Stats Section */}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {/* Stages Card */}
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
                { flex: 1, padding: 12, gap: 6 }
              ]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <MaterialCommunityIcons
                    name="stairs"
                    size={18}
                    color={theme ? '#3b82f6' : '#60a5fa'}
                  />
                  <RNText style={{
                    color: theme ? '#3b82f6' : '#60a5fa',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Stages
                  </RNText>
                </View>
                <RNText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 20,
                  fontWeight: '700'
                }}>
                  {totalStages}
                </RNText>
              </View>

              {/* Actions Card */}
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
                { flex: 1, padding: 12, gap: 6 }
              ]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <MaterialCommunityIcons
                    name="lightning-bolt"
                    size={18}
                    color={theme ? '#f59e0b' : '#fbbf24'}
                  />
                  <RNText style={{
                    color: theme ? '#f59e0b' : '#fbbf24',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Actions
                  </RNText>
                </View>
                <RNText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 20,
                  fontWeight: '700'
                }}>
                  {totalActions}
                </RNText>
              </View>

              {/* Executions Card */}
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
                { flex: 1, padding: 12, gap: 6 }
              ]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <MaterialIcons
                    name="play-circle-outline"
                    size={18}
                    color={theme ? '#10b981' : '#34d399'}
                  />
                  <RNText style={{
                    color: theme ? '#10b981' : '#34d399',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Runs
                  </RNText>
                </View>
                <RNText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 20,
                  fontWeight: '700'
                }}>
                  {totalExecutions}
                </RNText>
              </View>
            </View>

            {/* Analytics Section */}
            {totalExecutions > 0 && (
              <View style={[
                globalStyles.modernCardSmall,
                theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
                { padding: 12, gap: 10 }
              ]}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <RNText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 13,
                    fontWeight: '600'
                  }}>
                    Completion Rate
                  </RNText>
                  <RNText style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontSize: 14,
                    fontWeight: '700'
                  }}>
                    {completionRate.toFixed(1)}%
                  </RNText>
                </View>

                {/* Progress Bar */}
                <View style={{
                  height: 8,
                  backgroundColor: theme ? '#e2e8f0' : '#334155',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <View style={{
                    height: '100%',
                    width: `${completionRate}%`,
                    backgroundColor: completionRate >= 75
                      ? (theme ? '#10b981' : '#34d399')
                      : completionRate >= 50
                        ? (theme ? '#f59e0b' : '#fbbf24')
                        : (theme ? '#ef4444' : '#f87171'),
                    borderRadius: 4
                  }} />
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <RNText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 12
                  }}>
                    {completedExecutions} completed
                  </RNText>
                  <RNText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 12
                  }}>
                    {totalExecutions - completedExecutions} incomplete
                  </RNText>
                </View>
              </View>
            )}

            {/* Action Buttons */}
            <View style={{ flexDirection: 'row', gap: 10 }}>
              {/* Test Flow Button */}
              <TouchableOpacity
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark,
                  {
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                    borderRadius: 12,
                    minHeight: 50,
                    flex: 1
                  }
                ]}
                onPress={() => {
                  // Open CallModal to test the flow via voice call
                  dispatch({
                    type: SHOW_CALL_MODAL,
                    status: "testing",
                    payload: {
                      handle: item.title || 'Test Flow',
                      callUUID: item._id || 'test-flow-call',
                      avatar: item.createdBy?.profile_information?.avatar?.img,
                      flowId: item._id,
                      flowData: item
                    }
                  });
                }}
              >
                <MaterialCommunityIcons name="play-circle" size={20} color="white" />
                <RNText style={{
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 15,
                  marginLeft: 8
                }}>
                  Test
                </RNText>
              </TouchableOpacity>

              {/* Edit Flow Button */}
              <TouchableOpacity
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark,
                  {
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                    borderRadius: 12,
                    minHeight: 50,
                    flex: 1
                  }
                ]}
                onPress={() => {
                  // Navigate directly to FlowEditor (we're already in FlowStack)
                  navigation.navigate('FlowEditor', {
                    flowId: item._id,
                    mode: 'edit'
                  });
                }}
              >
                <MaterialIcons name="edit" size={20} color="white" />
                <RNText style={{
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 15,
                  marginLeft: 8
                }}>
                  Edit
                </RNText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
});

export default FlowItem;